"use client";
import React, { useRef, useEffect, useState } from "react";
import { useTheme } from "next-themes";

interface Particle {
  x: number;
  y: number;
  size: number;
  speedX: number;
  speedY: number;
  baseX: number;
  baseY: number;
  text: string;
  opacity: number;
  update: () => void;
  draw: (ctx: CanvasRenderingContext2D) => void;
}

export const CodeCanvas = () => {
  const { theme, resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !mounted) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let particles: Particle[] = [];
    const mouse = { x: 0, y: 0, radius: 150 };

    const codeGlyphs = [
      "{ }",
      "</>",
      "() =>",
      "UI",
      "UX",
      "01",
      "0xFF",
      "const",
      "let",
      "var",
      "async",
      "await",
      "fn()",
      "class",
      "return",
      "dart",
      "flutter",
      "widget",
      "build()",
    ];

    const isDark = resolvedTheme === "dark";

    class ParticleClass implements Particle {
      x: number;
      y: number;
      size: number;
      speedX: number;
      speedY: number;
      baseX: number;
      baseY: number;
      text: string;
      opacity: number;

      constructor(canvasWidth: number, canvasHeight: number) {
        this.x = Math.random() * canvasWidth;
        this.y = Math.random() * canvasHeight;
        this.baseX = this.x;
        this.baseY = this.y;
        this.size = Math.random() * 12 + 8;
        this.speedX = (Math.random() - 0.5) * 0.3;
        this.speedY = (Math.random() - 0.5) * 0.3;
        this.text = codeGlyphs[Math.floor(Math.random() * codeGlyphs.length)];
        this.opacity = Math.random() * (isDark ? 0.15 : 0.4) + 0.1;
      }

      draw(ctx: CanvasRenderingContext2D) {
        const color = isDark ? "rgba(200, 210, 255," : "rgba(200, 200, 200,";
        ctx.fillStyle = `${color} ${this.opacity})`;
        ctx.font = `${this.size}px 'JetBrains Mono', 'Courier New', monospace`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(this.text, this.x, this.y);
      }

      update() {
        // Drift movement
        this.x += this.speedX;
        this.y += this.speedY;

        // Screen wrap
        if (this.x > canvas!.width) this.x = 0;
        if (this.x < 0) this.x = canvas!.width;
        if (this.y > canvas!.height) this.y = 0;
        if (this.y < 0) this.y = canvas!.height;

        // Mouse Interaction
        let dx = mouse.x - this.x;
        let dy = mouse.y - this.y;
        let distance = Math.sqrt(dx * dx + dy * dy);

        if (distance < mouse.radius) {
          const forceDirectionX = dx / distance;
          const forceDirectionY = dy / distance;
          const force = (mouse.radius - distance) / mouse.radius;
          const directionX = forceDirectionX * force * 4;
          const directionY = forceDirectionY * force * 4;
          this.x -= directionX;
          this.y -= directionY;
        } else {
            // Slowly return to base speed if pushed far
            // (Optional: can add return-to-base logic here if desired)
        }
      }
    }

    const init = () => {
      particles = [];
      const numberOfParticles = (canvas.width * canvas.height) / 35000;
      for (let i = 0; i < numberOfParticles; i++) {
        particles.push(new ParticleClass(canvas.width, canvas.height));
      }
    };

    let animationFrameId: number;
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach((p) => {
        p.update();
        p.draw(ctx);
      });
      animationFrameId = requestAnimationFrame(animate);
    };

    const handleResize = () => {
      if (typeof window !== "undefined") {
        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;
        init();
      }
    };

    const handleMouseMove = (e: MouseEvent) => {
      mouse.x = e.clientX;
      mouse.y = e.clientY;
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("mousemove", handleMouseMove);
    handleResize();
    animate();

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("mousemove", handleMouseMove);
      cancelAnimationFrame(animationFrameId);
    };
  }, [mounted, resolvedTheme]);

  return (
    <canvas
      ref={canvasRef}
      className={`absolute inset-0 pointer-events-none ${
        resolvedTheme === "dark" ? "opacity-30" : "opacity-100"
      } transition-opacity duration-1000`}
      style={{ filter: "blur(0.3px)" }}
    />
  );
};
